import { FunctionComponent, useEffect, useState } from "react"
import {
  RootState,
  useTypedDispatch,
  useTypedSelector,
} from "../../../app/store"
import { addChoice } from "../../simulation/simulationSlice"
import { Information } from "../../simulation/types"
import {
  setActiveChoice,
  setActiveChoiceConnections,
  setActiveSearchQuery,
  setAddConnectionOpenFlag,
  setSelectedTreeItem,
  setTreeSearchResults,
} from "../decisionsSlice"
import { Choice, ChoiceType, TreeItemNode } from "../types"
import { createTreeNode, searchTreeFuzzy } from "../utils"
// import { rootsShootsActionPermissions } from "../../collaboration/rootsShootsContext"
import { useCollaboration } from "../../collaboration/hooks/useCollaboration"
import AddConnection from "./add-connection"
import CustomDecision from "./custom-decision"
import DecisionPlanButtons from "./decision-plan-buttons"
import Tree from "./tree"

const DecisionTreeAndSearch: FunctionComponent = () => {
  const dispatch = useTypedDispatch()
  const [treeItems, setTreeItems] = useState<TreeItemNode[]>([])
  const [searchTerm, setSearchTerm] = useState("")
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("")
  const [showCustomDecision, setShowCustomDecision] = useState(false)

  const simulationStore = useTypedSelector((state: RootState) => {
    return state.simulationSlice
  })

  const {
    selectedTreeItem,
    activeChoiceType,
    addConnectionOpenFlag: addConnectionFlag,
    activeChoice,
    activeChoiceConnections,
  } = useTypedSelector((state: RootState) => {
    return state.decisionsSlice
  })

  // Use collaboration hook for proper functionality
  const collaboration = useCollaboration()

  // Safely access collaboration state with fallbacks
  const collaborationState = useTypedSelector((state: RootState) => {
    try {
      if (state.collaborationSlice) {
        return {
          currentRoom: state.collaborationSlice.currentRoom,
          currentUserId: state.collaborationSlice.currentUserId,
          participants: state.collaborationSlice.participants || []
        }
      }
    } catch (error) {
      console.warn('Collaboration state not available in DecisionTreeAndSearch:', error)
    }
    return {
      currentRoom: null,
      currentUserId: null,
      participants: []
    }
  })

  const { currentRoom, currentUserId } = collaborationState
  const isCollaborative = currentRoom !== null

  const convertToTreeNodes = (information: Information[]): TreeItemNode[] => {
    return information.map((information) => {
      return createTreeNode(information)
    })
  }

  const handleInputChange = (e: { target: { value: string } }) => {
    setSearchTerm(e.target.value)
  }

  const resetSearch = () => {
    setSearchTerm("")
    setDebouncedSearchTerm("")
    setShowCustomDecision(false)
    dispatch(setTreeSearchResults([]))
    dispatch(setActiveSearchQuery(""))
    // mark all tree item nodes as not expanded when starting search
    // iterate over the tree nodes in place
    treeItems.forEach((node) => {
      node.isExpanded = false
    })
  }

  const resetActiveSearchState = () => {
    setShowCustomDecision(false)
    dispatch(setSelectedTreeItem(null))
    dispatch(setTreeSearchResults([]))
    dispatch(setActiveSearchQuery(debouncedSearchTerm))
  }

  const expandAll = (children: TreeItemNode[] | null = null) => {
    const nodes = children != null ? children : treeItems
    // Appears twice because we optimize by all expanding roots first
    nodes.forEach((node) => {
      node.isExpanded = true
      expandAll(node.children)
    })
  }

  const collapseAll = (children: TreeItemNode[] | null = null) => {
    const nodes = children != null ? children : treeItems
    nodes.forEach((node) => {
      node.isExpanded = false
      collapseAll(node.children)
    })
  }

  const handleCustomDecisionMade = () => {
    resetSearch()
  }

  const displayCustomDecision = () => {
    if (!showCustomDecision) return
    if (activeChoiceType === ChoiceType.search) {
      return <div>No information found, please try again</div>
    } else {
      return <CustomDecision onDecisionMade={handleCustomDecisionMade} />
    }
  }

  const fireSearchTermEvents = () => {
    const treeSearchInput = document.getElementById("treeSearchInput")
    if (!treeSearchInput) return
    document.dispatchEvent(
      new CustomEvent("treeSearchDebounced", {
        detail: { debouncedSearchTerm },
      }),
    )
  }

  // Collaboration permission logic
  // RULE: Only information searches are individual, ALL other choices require team voting
  const isIndividualChoice = (choiceType: ChoiceType): boolean => {
    return choiceType === ChoiceType.search
  }

  // Handle submission when connections are added
  // This ensures choices with connections also go through collaborative voting
  const handleConnectionsSubmit = async () => {
    if (!activeChoiceType || !selectedTreeItem) return

    const item = Object.assign({}, selectedTreeItem) as Information
    item.categoryId = item.id
    item.id = simulationStore.messages.length + 1

    const choice = {
      choiceType: activeChoiceType,
      item: item,
      time: Date.now(),
      connections: activeChoiceConnections || [],
    } as Choice

    // Check if we're in collaborative mode and if this decision requires voting
    if (isCollaborative && currentRoom && currentUserId) {
      try {
        // Simple rule: Only information searches are individual, everything else requires voting
        if (!isIndividualChoice(activeChoiceType)) {
          // This decision requires team voting - create collaborative choice proposal
          console.log(`Creating collaborative choice proposal with connections for ${activeChoiceType}`)

          try {
            await collaboration.proposeChoice({
              choiceType: activeChoiceType,
              item: item,
              connections: activeChoiceConnections || []
            })

            console.log('Collaborative choice with connections proposed successfully')
            dispatch(setSelectedTreeItem(null))
            dispatch(setActiveChoiceConnections([]))
            dispatch(setAddConnectionOpenFlag(false))
            return
          } catch (error) {
            console.error('Failed to propose collaborative choice with connections:', error)
            // Fallback to individual choice if collaboration fails
            console.log('Falling back to individual choice')
          }
        }
      } catch (collaborationError) {
        console.warn('Error handling collaborative decision with connections:', collaborationError)
      }
    }

    // Either not in collaborative mode, or this is an individual action
    dispatch(addChoice(choice))
    dispatch(setSelectedTreeItem(null))
    dispatch(setActiveChoiceConnections([]))
    dispatch(setAddConnectionOpenFlag(false))
  }

  const handleDecisionsClick = async () => {
    if (!activeChoiceType || !selectedTreeItem) return
    
    const item = Object.assign({}, selectedTreeItem) as Information
    item.categoryId = item.id
    item.id = simulationStore.messages.length + 1
    
    const choice = {
      choiceType: activeChoiceType,
      item: item,
      time: Date.now(),
    } as Choice

    // Check if we're in collaborative mode and if this decision requires voting
    if (isCollaborative && currentRoom && currentUserId) {
      try {
        // Simple rule: Only information searches are individual, everything else requires voting
        if (!isIndividualChoice(activeChoiceType)) {
          // This decision requires team voting - create collaborative choice proposal
          console.log(`Creating collaborative choice proposal for ${activeChoiceType}`)
          
          try {
            await collaboration.proposeChoice({
              choiceType: activeChoiceType,
              item: item,
              connections: activeChoice?.connections || []
            })
            
            console.log('Collaborative choice proposed successfully')
            dispatch(setSelectedTreeItem(null))
            return
          } catch (error) {
            console.error('Failed to propose collaborative choice:', error)
            // Fallback to individual choice if collaboration fails
            console.log('Falling back to individual choice')
          }
        } else {
          // Information search - processed individually and privately
          console.log('Information search - processing individually (private to user)')
        }
      } catch (collaborationError) {
        console.warn('Error handling collaborative decision, proceeding with individual decision:', collaborationError)
      }
    }

    // Either not in collaborative mode, or this is an individual action that doesn't require voting
    dispatch(addChoice(choice))
    dispatch(setSelectedTreeItem(null))
  }

  const buttonDisabledCheck = (type: string) => {
    // This is to disable Add connection for Information
    if (
      activeChoiceType != null &&
      activeChoiceType === ChoiceType.search &&
      type === ChoiceType.add
    ) {
      return (
        !activeChoiceType ||
        !selectedTreeItem ||
        activeChoiceType === ChoiceType.search
      )
    } else {
      return !activeChoiceType || !selectedTreeItem
    }
  }

  useEffect(() => {
    // Filtering only when the length of the search input exceeds 3 characters
    if (debouncedSearchTerm.length >= 3) {
      resetActiveSearchState()
      fireSearchTermEvents()
      const results = Array.from(
        searchTreeFuzzy(treeItems, debouncedSearchTerm),
      )
      if (results.length > 0) {
        dispatch(setTreeSearchResults(results))
      } else {
        setShowCustomDecision(true)
      }
    } else if (debouncedSearchTerm.length === 0) {
      resetSearch()
      fireSearchTermEvents()
    } else {
      dispatch(setTreeSearchResults([]))
    }
  }, [debouncedSearchTerm])

  useEffect(() => {
    if (!activeChoiceType) return

    // Switching panels should reset the search
    resetActiveSearchState()
    setSearchTerm("")
    setDebouncedSearchTerm("")

    if (
      activeChoiceType === ChoiceType.search &&
      simulationStore.information[0]
    ) {
      setTreeItems(
        convertToTreeNodes(simulationStore.information[0].categories),
      )
    } else {
      setTreeItems(convertToTreeNodes(simulationStore.choices))
    }
  }, [activeChoiceType])

  useEffect(() => {
    if (!selectedTreeItem) return

    // fire custom event with data about selected node
    document.dispatchEvent(
      new CustomEvent("treeNodeSelected", {
        detail: { selectedTreeItem, activeChoiceType },
      }),
    )

    if (activeChoiceType === ChoiceType.search) return
    const choice = {
      choiceType: activeChoiceType,
      item: selectedTreeItem,
      time: Date.now(),
    } as Choice
    dispatch(setActiveChoice(choice))
  }, [selectedTreeItem])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300)
    return () => clearTimeout(timeoutId)
  }, [searchTerm, 300])

  return (
    <div className="w-full flex flex-col flex-1 min-h-0 overflow-hidden">
      {!addConnectionFlag && activeChoiceType && (
        <div
          id="treeAndSearchContainer"
          className="flex flex-col flex-1 min-h-0 overflow-hidden pt-4">
          {/* Search bar and Expand/Collapse buttons - fixed at top */}
          <div className="flex-shrink-0 px-4 mb-2">
            <div className="flex flex-row items-center justify-between gap-2">
              <div className="flex-1 relative rounded-lg bg-base-white shadow-sm box-border overflow-hidden flex flex-row items-center justify-start py-2.5 px-3.5 text-left text-base text-gray-500 font-text-md-regular border-[1px] border-solid border-gray-300">
                <img
                  className="relative w-5 h-5 overflow-hidden shrink-0"
                  alt=""
                  src="/v3/assets/search-lg.svg"
                />
                <input
                  id="treeSearchInput"
                  className="w-full bg-transparent outline-none placeholder-gray-500 text-gray-700 pl-2.5 pr-3.5 leading-[24px] font-text-md-regular"
                  type="text"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={handleInputChange}
                />
                {searchTerm && (
                  <img
                    id="clearTreeSearchButton"
                    className="relative w-4 h-4 overflow-hidden shrink-0 cursor-pointer"
                    alt=""
                    src="/v3/assets/circle-xmark.svg"
                    onClick={() => resetSearch()}
                  />
                )}
              </div>
              {/* Expand/Collapse buttons - inline with search */}
              {!showCustomDecision && (
                <div className="flex flex-row gap-1 flex-shrink-0">
                  <button
                    className="btn btn-xs btn-outline rounded-sm text-black font-normal"
                    onClick={() => expandAll()}>
                    Expand
                  </button>
                  <button
                    className="btn btn-xs btn-outline rounded-sm text-black font-normal"
                    onClick={() => collapseAll()}>
                    Collapse
                  </button>
                </div>
              )}
            </div>
          </div>
          {/* Scrollable tree container - takes remaining space */}
          <div
            id="tree-container"
            className="flex-1 overflow-y-auto min-h-0 px-4 text-sm text-gray-700">
            {!showCustomDecision && (
              <div className="w-full flex flex-col items-start justify-start space-y-6 pr-2">
                <Tree choices={treeItems} />
              </div>
            )}
            {displayCustomDecision()}
          </div>
          {/* Buttons - fixed at bottom of container via flex layout */}
          {/* flex-shrink-0 ensures buttons never shrink, parent flex layout keeps them at bottom */}
          <div className="flex-shrink-0 px-4 py-4 bg-white border-t border-gray-200">
            <DecisionPlanButtons
              handleDecisionsClick={handleDecisionsClick}
              disabledCheck={buttonDisabledCheck}
              hideConnections={isCollaborative}
            />
          </div>
        </div>
      )}
      {/* AddConnection panel - hidden for team simulations (isCollaborative) */}
      {!isCollaborative && addConnectionFlag && activeChoice && (
        <div
          id="addConnectionContainer"
          className="flex flex-col flex-1 min-h-0 overflow-hidden">
          <AddConnection choice={activeChoice} submitAction={handleConnectionsSubmit} />
        </div>
      )}
    </div>
  )
}

export default DecisionTreeAndSearch
